import React from "react";
import BaseInputMethodCalculationComponentInterface from "./BaseInputMethodCalculationComponentInterface";
import ExpandableContainer from "../../../configuration/steps/ExpandableContainer";
import TWSCalculationApiService from "../../../../api-services/TWSCalculationApiService";
import { TWSInputMethodFields } from "../Interfaces";
import BaseInputValidator from "../../../common/input-validators/BaseInputValidator";
import { Calculation, Selection } from "../../../../../src/application/Contracts";
import AquaFirstTable from "./product-catalog-tech-data/AquaFirstTable";
import AquaGeniusTable from "./product-catalog-tech-data/AquaGeniusTable";
import AquaEfficiencyTable from "./product-catalog-tech-data/AquaEfficiencyTable";
import AquaPlateTable from "./product-catalog-tech-data/AquaPlateTable";
import AlfaPilotTable from "./product-catalog-tech-data/AlfaPilotTable";
import formModelStore from "../../../../store/FormModelStore";
import ProductFamily = Selection.ProductFamily;
import getProductFamilyName = Selection.getProductFamilyName;
import { upperFirst } from "lodash";
import { StepsSlugs } from "../TWSProjectCalculationContainer";
import { ShowTankSelectionStep } from "../../../utils/FormModelStoreTWSHelper";
import ProductDataModel = Calculation.ProductDataModel;

interface SelfProps {
    apiService: TWSCalculationApiService | null,
    fields: TWSInputMethodFields,
    renderField: (field: any, additionalProps?: object, validator?: BaseInputValidator | undefined) => any,
    updateStepAvailability: (stepSlug: StepsSlugs, available: boolean) => void;
    restoredProduct: ProductDataModel;
}

interface SelfState {
    selectedProduct?: Calculation.ProductCatalogItemModel
}

export class ProductCatalogInputMethod extends React.Component<SelfProps, Calculation.ProductDataModel & SelfState> implements BaseInputMethodCalculationComponentInterface {
    constructor(props: SelfProps) {
        super(props);
        this.state = {
            description: this.props.restoredProduct?.description ?? "",
            mainPhoto: this.props.restoredProduct?.mainPhoto ?? "",
            name: this.props.restoredProduct?.name ?? "",
            displayName: this.props.restoredProduct?.displayName ?? "",
            defaultTemperatureProgramForTable: this.props.restoredProduct?.defaultTemperatureProgramForTable ?? "",
            technicalData: {
                aquaEfficiencyTechnicalData: this.props.restoredProduct?.technicalData?.aquaEfficiencyTechnicalData ?? [],
                aquaPilotTechnicalData: this.props.restoredProduct?.technicalData?.aquaPilotTechnicalData ?? [],
                aquaPlateTechnicalData: this.props.restoredProduct?.technicalData?.aquaPlateTechnicalData ?? [],
                aquaFirstTechnicalData: this.props.restoredProduct?.technicalData?.aquaFirstTechnicalData ?? [],
                aquaGeniusTechnicalData: this.props.restoredProduct?.technicalData?.aquaGeniusTechnicalData ?? []
            }
        }
        window.scheduleAtDocumentReady(() => {
            const product = formModelStore.formModel?.inputMethod?.productCatalogCalculation?.selectedProduct?.value;
            if (product) {
                this.setState({
                    selectedProduct: product
                })
                if (product.name === upperFirst(ProductFamily.AquaTank))
                    this.props.updateStepAvailability(StepsSlugs.TankSelection, true);
            }

        })
    }

    render() {
        return <React.Fragment>
            <div className={`step-row product-catalog-input-method`}>
                <div className={`card`}>
                    <div className={`card-body`}>
                        <ExpandableContainer arrowFirst={true} label={__('Calculate method')}
                            initialOpen={true}>
                            {this.props.renderField(this.props.fields.selectedProduct, {
                                additionalModel: {
                                    items: this.props.fields.productCatalogProductFamilies.value,
                                    onProductSelect: this.onProductSelect
                                },
                            })}
                        </ExpandableContainer>
                        {this.state.description && <ExpandableContainer arrowFirst={true} label={__('Description')}
                            initialOpen={true}>
                            <div className={`grid grid-2 description-section`}>
                                <div className={`image`} style={{ backgroundImage: `url(${this.state.mainPhoto}` }} />
                                <div className={`description`}>
                                    <div className={`name`}>{this.state.displayName}</div>
                                    <div className={`description-text`}
                                        dangerouslySetInnerHTML={{ __html: this.state.description }} />
                                    {this.renderOperationLimitsTable()}
                                </div>
                            </div>
                        </ExpandableContainer>}
                        {this.renderTechnicalDataTable()}
                    </div>
                </div>
            </div>
        </React.Fragment>
    }

    renderTechnicalDataTable = () => {
        if (this.state.technicalData?.aquaPlateTechnicalData?.length) {
            return <AquaPlateTable temperatureProgram={this.state.defaultTemperatureProgramForTable} renderField={this.props.renderField} items={this.state.technicalData?.aquaPlateTechnicalData} fields={this.props.fields} />
        }
        if (this.state.technicalData?.aquaPilotTechnicalData?.length) {
            return <AlfaPilotTable temperatureProgram={this.state.defaultTemperatureProgramForTable} renderField={this.props.renderField} items={this.state.technicalData?.aquaPilotTechnicalData} fields={this.props.fields} />
        }
        if (this.state.technicalData?.aquaEfficiencyTechnicalData?.length) {
            return <AquaEfficiencyTable temperatureProgram={this.state.defaultTemperatureProgramForTable} renderField={this.props.renderField} items={this.state.technicalData?.aquaEfficiencyTechnicalData} fields={this.props.fields} />
        }
        if (this.state.technicalData?.aquaGeniusTechnicalData?.length) {
            return <AquaGeniusTable temperatureProgram={this.state.defaultTemperatureProgramForTable} renderField={this.props.renderField} items={this.state.technicalData?.aquaGeniusTechnicalData} fields={this.props.fields} />
        }
        if (this.state.technicalData?.aquaFirstTechnicalData?.length) {
            return <AquaFirstTable temperatureProgram={this.state.defaultTemperatureProgramForTable} renderField={this.props.renderField} items={this.state.technicalData?.aquaFirstTechnicalData} fields={this.props.fields} />
        }
        return <></>
    }

    renderOperationLimitsTable = () => {
        let hasPrimaryData = (this.state.primaryDesignPressure && this.state.primaryDesignTemperature);
        let hasSecondaryData = (this.state.secondaryDesignPressure && this.state.secondaryDesignTemperature);
        if (!hasPrimaryData && !hasSecondaryData) {
            return <div />;
        }
        return <div className={`small-stripped-data-table`}>
            <table>
                <thead>
                    <tr>
                        <th>{__("Operating limits")}</th>
                        <th>
                            <div className={`information-header`}>
                                {__("Design temperature")}
                                <svg width="17" height="16" viewBox="0 0 17 16" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M8.33398 14C11.6477 14 14.334 11.3137 14.334 8C14.334 4.68629 11.6477 2 8.33398 2C5.02028 2 2.33398 4.68629 2.33398 8C2.33398 11.3137 5.02028 14 8.33398 14Z"
                                        stroke="#757575" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M7.83398 7.5H8.33398V11H8.83398" stroke="#757575" strokeLinecap="round"
                                        strokeLinejoin="round" />
                                    <path
                                        d="M8.20898 6C8.6232 6 8.95898 5.66421 8.95898 5.25C8.95898 4.83579 8.6232 4.5 8.20898 4.5C7.79477 4.5 7.45898 4.83579 7.45898 5.25C7.45898 5.66421 7.79477 6 8.20898 6Z"
                                        fill="#757575" />
                                </svg>
                                <div className={`hover-information`}>
                                    {__("In accordance with each country's legislation")}
                                </div>
                            </div>
                        </th>
                        <th>{__("Design pressure")}</th>
                    </tr>
                </thead>
                <tbody>
                    {hasPrimaryData && <tr>
                        <td>{__("Primary")}</td>
                        <td>{`${this.state.primaryDesignTemperature} °C`}</td>
                        <td>{`${this.state.primaryDesignPressure} bar`}</td>
                    </tr>}
                    {hasSecondaryData && <tr>
                        <td>{__("Secondary")}</td>
                        <td>{`${this.state.secondaryDesignTemperature} °C`}</td>
                        <td>{`${this.state.secondaryDesignPressure} bar`}</td>
                    </tr>}
                </tbody>
            </table>
        </div>
    }
    onProductSelect = (product: Calculation.ProductCatalogItemModel) => {
        this.setState({
            selectedProduct: product
        })
        this.props.updateStepAvailability(StepsSlugs.WaterSystem, product.name !== upperFirst(ProductFamily.AquaTank));
        this.props.updateStepAvailability(StepsSlugs.TankSelection, product.name === upperFirst(ProductFamily.AquaTank));
        this.props.apiService?.getSelectedProductData(product).then(response => {
            this.props.fields?.temperatureProgram?.setValue(response.data.defaultTemperatureProgramForTable)
            this.setState(response.data)
        })
    }

    canProceed(): boolean {
        return this.props.fields?.selectedProduct?.value?.name?.length > 0;
    }

    componentDidMount() {
        if (formModelStore.formModel?.inputMethod?.productCatalogCalculation?.selectedProduct?.value?.name?.length > 0) {
            this.props.fields?.selectedProduct?.setValue(formModelStore.formModel?.inputMethod?.productCatalogCalculation?.selectedProduct?.value)
            this.onProductSelect(formModelStore.formModel?.inputMethod?.productCatalogCalculation?.selectedProduct?.value)
        }
    }

    isValidFormModel(parameter: any): boolean {
        return this.props.fields?.selectedProduct?.value?.name?.length > 0;
    }
}